import React from "react";
import { Box, Button, Collapse, Grid, makeStyles, Typography } from "@material-ui/core";
import SelectLocation from "../../select/SelectLocation";
import SelectWithUserRoles from "../../select/SelectWithUserRoles";
import SelectUserSituations from "../../select/SelectUserSituations";
import MonthYearSelector from "../../MonthYearSelect";
import SelectAgeRanges from "./SelectAgeRange";
import SearchByNameOrId from "../../SearchByNameOrId";
import SelectMultiBoolean from "../../select/SelectMultiBoolean";
import ReportGroupSelect from "./byGroup/ReportGroupSelect";
import { FormattedMessage } from "react-intl";
import { Roles, USER_FILTER_KEYS } from "../../../../util";

const useStyles = makeStyles((theme) => ({
  containerTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  containerSubtitle: {
    marginBottom: theme.spacing(2),
  },
}));

const DefaultWrapper = React.memo(({ children }) => (
  <Grid
    item
    xs={12}
    sm={6}
    md={4}
  >
    {children}
  </Grid>
));

const UserFilters = ({ userFilters, setUserFilters, fetchCitiesEnabled, fetchStatesEnabled, WrapperComponent = DefaultWrapper }) => {
  const classes = useStyles();
  const [openCollapse, setOpenCollapse] = React.useState(true);

  const handleFilterChange = (name, value) => {
    setUserFilters((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <Box className={classes.containerTitle}>
        <Typography variant="h6">
          <FormattedMessage id="app.filters" />
        </Typography>

        <Button onClick={() => setOpenCollapse((prev) => !prev)}>
          <FormattedMessage id={openCollapse ? "app.filters.hide" : "app.filters.show"} />
        </Button>
      </Box>

      <Collapse
        in={openCollapse}
        timeout={500}
      >
        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.containerSubtitle}
        >
          <FormattedMessage id="app.filters.instruction.metrics" />
        </Typography>

        <Grid
          container
          spacing={2}
        >
          {userFilters.hasOwnProperty(USER_FILTER_KEYS.COUNTRIES) && (
            <SelectLocation
              fetchStatesEnabled={fetchStatesEnabled}
              fetchCitiesEnabled={fetchCitiesEnabled}
              selectedCountries={userFilters[USER_FILTER_KEYS.COUNTRIES]}
              selectedStates={userFilters[USER_FILTER_KEYS.STATES]}
              selectedCities={userFilters[USER_FILTER_KEYS.CITIES]}
              setSelectedCountries={(value) => handleFilterChange(USER_FILTER_KEYS.COUNTRIES, value)}
              setSelectedStates={(value) => handleFilterChange(USER_FILTER_KEYS.STATES, value)}
              setSelectedCities={(value) => handleFilterChange(USER_FILTER_KEYS.CITIES, value)}
              Wrapper={WrapperComponent}
            />
          )}

          {userFilters.hasOwnProperty(USER_FILTER_KEYS.ROLES) && (
            <WrapperComponent>
              <SelectWithUserRoles
                selectedUserRoles={userFilters[USER_FILTER_KEYS.ROLES]}
                setSelectedUserRoles={(values) => handleFilterChange(USER_FILTER_KEYS.ROLES, values)}
              />
            </WrapperComponent>
          )}

          {userFilters.hasOwnProperty(USER_FILTER_KEYS.CUSTOMER_SITUATIONS) && (
            <WrapperComponent>
              <SelectUserSituations
                enableInapt
                selectedUserSituations={userFilters[USER_FILTER_KEYS.CUSTOMER_SITUATIONS]}
                setSelectedUserSituations={(values) => handleFilterChange(USER_FILTER_KEYS.CUSTOMER_SITUATIONS, values)}
                title="app.situation.customer"
              />
            </WrapperComponent>
          )}

          {userFilters.hasOwnProperty(USER_FILTER_KEYS.AGENT_SITUATIONS) && (
            <WrapperComponent>
              <SelectUserSituations
                selectedUserSituations={userFilters[USER_FILTER_KEYS.AGENT_SITUATIONS]}
                setSelectedUserSituations={(values) => handleFilterChange(USER_FILTER_KEYS.AGENT_SITUATIONS, values)}
                title="app.situation.agent"
              />
            </WrapperComponent>
          )}

          {userFilters.hasOwnProperty(USER_FILTER_KEYS.AGE_RANGES) && (
            <WrapperComponent>
              <SelectAgeRanges
                selectedAgeRanges={userFilters[USER_FILTER_KEYS.AGE_RANGES]}
                setSelectedAgeRanges={(values) => handleFilterChange(USER_FILTER_KEYS.AGE_RANGES, values)}
              />
            </WrapperComponent>
          )}

          {userFilters.hasOwnProperty(USER_FILTER_KEYS.EMAIL_CONFIRMATION) && (
            <WrapperComponent>
              <SelectMultiBoolean
                title="app.report.emailConfirmation"
                trueLabel="app.email.confirmed"
                falseLabel="app.email.confirmed.no"
                selectedValues={userFilters[USER_FILTER_KEYS.EMAIL_CONFIRMATION]}
                setSelectedValues={(values) => handleFilterChange(USER_FILTER_KEYS.EMAIL_CONFIRMATION, values)}
              />
            </WrapperComponent>
          )}

          {userFilters.hasOwnProperty(USER_FILTER_KEYS.HAS_BENEFICIARY) && (
            <WrapperComponent>
              <SelectMultiBoolean
                title="app.report.hasBeneficiary"
                trueLabel="app.report.hasBeneficiary.true"
                falseLabel="app.report.hasBeneficiary.false"
                selectedValues={userFilters[USER_FILTER_KEYS.HAS_BENEFICIARY]}
                setSelectedValues={(values) => handleFilterChange(USER_FILTER_KEYS.HAS_BENEFICIARY, values)}
              />
            </WrapperComponent>
          )}

          {userFilters.hasOwnProperty(USER_FILTER_KEYS.SELECTED_MONTH_YEAR) && (
            <WrapperComponent>
              <MonthYearSelector
                date={userFilters[USER_FILTER_KEYS.SELECTED_MONTH_YEAR]}
                setDate={(value) => handleFilterChange(USER_FILTER_KEYS.SELECTED_MONTH_YEAR, value)}
              />
            </WrapperComponent>
          )}

          {userFilters.hasOwnProperty(USER_FILTER_KEYS.GROUP) && (
            <WrapperComponent>
              <ReportGroupSelect
                selectedValue={userFilters[USER_FILTER_KEYS.GROUP]}
                setSelectedValue={(value) => handleFilterChange(USER_FILTER_KEYS.GROUP, value)}
              />
            </WrapperComponent>
          )}

          {userFilters.hasOwnProperty(USER_FILTER_KEYS.USERS) && (
            <Grid
              item
              xs={12}
            >
              <SearchByNameOrId
                title="app.searchCustomers"
                label="app.customer"
                selectedUsers={userFilters[USER_FILTER_KEYS.USERS]}
                setSelectedUsers={(values) => handleFilterChange(USER_FILTER_KEYS.USERS, values)}
                roles={[Roles.Customer, Roles.Agent]}
                enableSearchById
              />
            </Grid>
          )}

          {userFilters.hasOwnProperty(USER_FILTER_KEYS.AGENTS) && (
            <Grid
              item
              xs={12}
            >
              <SearchByNameOrId
                title="app.searchAgent"
                label="app.agent"
                selectedUsers={userFilters[USER_FILTER_KEYS.AGENTS]}
                setSelectedUsers={(values) => handleFilterChange(USER_FILTER_KEYS.AGENTS, values)}
                roles={[Roles.Agent]}
                enableSearchById
              />
            </Grid>
          )}
        </Grid>
      </Collapse>
    </>
  );
};

export default UserFilters;
